/*
 * Global app theme — shared with the Login page.
 *
 *  Login lets users pick from 15 named themes (Warm Stone, Midnight Steel,
 *  Carbon & Volt, …) and persists the choice to localStorage under
 *  'alpaca.login.theme'.  Until now that pick only affected the Login
 *  screen.  This hook plus applyAppTheme() propagates it to the entire
 *  shell so every form's chrome (popup title bars, table headers, hover
 *  states, etc.) follows the user's choice.
 *
 *  Usage:
 *    const t = useAppTheme();
 *    <div style={{ background: t.accent }} />
 *
 *  Or for static CSS rules — read the matching CSS custom property which
 *  applyAppTheme() sets on the document root:
 *    background: var(--brand-accent);
 *    background: var(--brand-feature-bg);
 *    border-color: var(--brand-accent-hover);
 *
 *  When the user picks a new theme (LoginPage.pick()), it calls
 *  applyAppTheme() — which writes the new CSS vars AND fires the
 *  'alpaca-theme-changed' event so live React subscribers re-render.
 */
import { useEffect, useSyncExternalStore } from 'react';
import { THEMES, type LoginTheme } from '../features/auth/themes';

export const THEME_STORAGE_KEY = 'alpaca.login.theme';
const THEME_EVENT_NAME         = 'alpaca-theme-changed';

/** Set by the Login page when a user picks a theme BEFORE authenticating (no JWT
 *  yet to save it).  AppShell reads this right after login, persists it to the
 *  per-user store, then clears it — so a login-page pick follows the user too. */
export const PENDING_THEME_SAVE_KEY = 'alpaca.theme.pendingSave';

/** Map theme id → THEMES entry; falls back to first theme when unknown. */
function resolveTheme(id: string | null): LoginTheme {
  const found = THEMES.find(t => t.id === id);
  return found ?? THEMES[0]!;
}

/** Read the currently active theme from localStorage (sync). */
export function getCurrentTheme(): LoginTheme {
  try {
    return resolveTheme(localStorage.getItem(THEME_STORAGE_KEY));
  } catch {
    return THEMES[0]!;
  }
}

/**
 * Write CSS custom properties to `:root` so any component (refactored or
 * not) can pick them up via `var(--brand-*)`.  Also dispatches a custom
 * event so React subscribers (useAppTheme) re-render immediately.
 *
 *  Variables exposed:
 *    --brand-accent          primary accent (orange in Warm Stone)
 *    --brand-accent-hover    accent hover state
 *    --brand-accent-glow     soft-alpha accent for shadows / glows
 *    --brand-feature-bg      light tinted background (peach in Warm Stone)
 *    --brand-feature-border  matching border tone
 *    --brand-text-on-accent  text colour over accent (usually #fff)
 *    --brand-divider         neutral divider that matches the theme
 *    --brand-text-dark       primary body text
 *    --brand-text-muted      secondary body text
 */
export function applyAppTheme(themeOrId?: LoginTheme | string): LoginTheme {
  const t: LoginTheme = typeof themeOrId === 'string'
    ? resolveTheme(themeOrId)
    : themeOrId ?? getCurrentTheme();

  // Persist (so a hard refresh keeps the choice).
  try { localStorage.setItem(THEME_STORAGE_KEY, t.id); } catch { /* ignore */ }

  if (typeof document !== 'undefined') {
    const root = document.documentElement.style;
    root.setProperty('--brand-accent',         t.accent);
    root.setProperty('--brand-accent-hover',   t.accentHover);
    root.setProperty('--brand-accent-glow',    t.accentGlow);
    root.setProperty('--brand-feature-bg',     t.featureBg);
    root.setProperty('--brand-feature-border', t.featureBorder);
    root.setProperty('--brand-text-on-accent', '#ffffff');
    root.setProperty('--brand-divider',        t.divider);
    root.setProperty('--brand-text-dark',      t.textDark);
    root.setProperty('--brand-text-muted',     t.textMuted);
    // Tell live subscribers to re-render with the new theme object.
    window.dispatchEvent(new CustomEvent(THEME_EVENT_NAME, { detail: t }));
  }
  return t;
}

/**
 * Reactive hook — returns the current LoginTheme and re-renders whenever
 * applyAppTheme() runs (own tab) or another tab updates localStorage
 * (cross-tab via the 'storage' event).
 *
 *  Built on useSyncExternalStore so React 18+ concurrent rendering reads
 *  a stable snapshot per render.
 */
export function useAppTheme(): LoginTheme {
  return useSyncExternalStore(
    (cb) => {
      const onStorage = (e: StorageEvent) => {
        if (e.key === THEME_STORAGE_KEY) cb();
      };
      const onCustom = () => cb();
      window.addEventListener('storage', onStorage);
      window.addEventListener(THEME_EVENT_NAME, onCustom);
      return () => {
        window.removeEventListener('storage', onStorage);
        window.removeEventListener(THEME_EVENT_NAME, onCustom);
      };
    },
    () => getCurrentTheme(),
    () => THEMES[0]!,   // SSR fallback (we don't SSR but the API requires this)
  );
}

/** localStorage flag — once set, the one-time migration to Office White
 *  has run for this browser and won't run again.  Bumping the suffix
 *  (v1 → v2) re-runs the migration for everyone. */
const THEME_MIGRATION_KEY = 'alpaca.theme-migrated-v1';

/**
 * Auto-apply hook — call once near the app root (e.g. in App.tsx) so
 * the CSS variables are populated on first render, before any consumer
 * tries to read them.  Subsequent picks on the Login screen propagate
 * via applyAppTheme().
 *
 * One-time migration (2026-06-07): we shipped "Office White" as the
 * new corporate default after most testers had already picked a
 * different theme (often Midnight Steel — blue) during development.
 * The first time each browser loads the app after this commit, we
 * force-reset their saved theme to 'office-white' and mark the
 * migration done.  After that the user is free to pick any theme on
 * the Login screen and it persists normally.
 */
export function useEnsureThemeApplied(): void {
  useEffect(() => {
    try {
      if (!localStorage.getItem(THEME_MIGRATION_KEY)) {
        localStorage.setItem(THEME_STORAGE_KEY, 'office-white');
        localStorage.setItem(THEME_MIGRATION_KEY, '1');
      }
    } catch { /* private browsing — ignore */ }
    applyAppTheme();
  }, []);
}
